// Canonical outreach status vocabulary + color map.
// Single source of truth used by DashboardPage, OutreachPage, CompanyDetailPage
// so a "Meeting Scheduled" chip looks identical on every screen.
//
// Statuses are listed in pipeline order (earliest → latest), with terminal /
// off-funnel statuses at the end. "Email Acknowledged" was re-added Jul 2026
// per user request (covers "passed this on to expert colleagues" replies).
// "Interest Shown" stays removed per user request 2026-02.

export const OUTREACH_STATUSES = [
  'Introduction Made',
  'Email Acknowledged',
  'Already in Touch',
  'Asset Under Evaluation',
  'Meeting Requested',
  'Meeting Scheduled',
  'CDA in Progress',
  'CDA Signed',
  'Data Shared / Data Room Access',
  'Awaiting Trial Data',
  'Due Diligence',
  'Deal Discussion',
  'Term Sheet Shared',
  'Deal Closed',
  'On Hold',
  'Declined',
  'Deal Lost',
  'Ghosted',
];

// Statuses we hide everywhere — kept here so backwards-compatible data
// (legacy rows with these statuses) doesn't show in dropdowns / funnels.
// Interest Shown stays dropped by user request; Email Acknowledged was
// re-added Jul 2026 by user request.
// Introduction Made + Already in Touch + Awaiting reply are funnel-stage
// noise per user 2026-02: account-driven introductions live on the
// dedicated "Account Intros" chip, and non-account intros aren't tracked
// as real conversations either.
// Meeting Completed + Business Discussion removed 2026-02 per user
// ("redundant — Meeting Scheduled covers the meeting state and
// Deal Discussion covers post-eval commercial talks").
// Aug 2026 · user directive: "put all those in the BD funnel so that
// everything gets streamlined." Only fully-deprecated / internal-signal
// statuses stay hidden — the three early-stage rows (Introduction Made,
// Email Acknowledged, Already in Touch) surface as top-of-funnel bars.
export const HIDDEN_STATUSES = new Set([
  'Interest Shown',
  'Awaiting reply',
  'Meeting Completed',
  'Business Discussion',
]);

// Active = in genuine BD engagement (post-intro, pre-terminal).
export const ACTIVE_STATUSES = [
  'Asset Under Evaluation',
  'Meeting Requested',
  'Meeting Scheduled',
  'CDA in Progress',
  'CDA Signed',
  'Data Shared / Data Room Access',
  'Awaiting Trial Data',
  'Due Diligence',
  'Deal Discussion',
  'Term Sheet Shared',
];

// Canonical color palette — each status gets a DISTINCT hue so the user can
// visually scan the pipeline. Positive / active stages use cool & saturated
// colors; terminal negatives use rose/stone; "On Hold" gets yellow (NOT
// amber, which is reserved for AUE).
export const OUTREACH_STATUS_COLORS = {
  // Intro
  'Introduction Made':       'bg-cyan-100 text-cyan-700',
  'Already in Touch':        'bg-teal-100 text-teal-700',
  // Active pipeline
  'Asset Under Evaluation':         'bg-amber-100 text-amber-700',
  'Meeting Requested':              'bg-violet-50 text-violet-600',
  'Meeting Scheduled':              'bg-violet-100 text-violet-700',
  'Meeting Completed':              'bg-fuchsia-100 text-fuchsia-700',
  'CDA in Progress':                'bg-blue-50 text-blue-600',
  'CDA Signed':                     'bg-blue-100 text-blue-700',
  'Data Shared / Data Room Access': 'bg-sky-100 text-sky-700',
  'Awaiting Trial Data':            'bg-orange-100 text-orange-700',
  'Due Diligence':                  'bg-indigo-100 text-indigo-700',
  'Business Discussion':            'bg-pink-100 text-pink-700',
  'Deal Discussion':                'bg-purple-100 text-purple-700',
  'Term Sheet Shared':              'bg-lime-100 text-lime-700',
  // Terminal positive
  'Deal Closed':             'bg-emerald-100 text-emerald-700',
  'Closed - Successful':     'bg-emerald-100 text-emerald-700',
  // Paused
  'On Hold':                 'bg-yellow-100 text-yellow-700',
  // Terminal negative
  'Declined':                'bg-rose-100 text-rose-700',
  'Deal Lost':               'bg-rose-100 text-rose-700',
  'Ghosted':                 'bg-stone-100 text-stone-700',
};

export const getStatusColor = (status) =>
  OUTREACH_STATUS_COLORS[status] || 'bg-slate-100 text-slate-600';

// Accounts → Outreach status normalization. The Accounts module uses its
// own vocabulary ("Closed-Won", "Not Interested", "NCD Exchanged") for
// historical reasons. Map them to the canonical outreach status so the
// dashboard backend (which canonicalizes via the same map in
// `routes/dashboard.py:ACCT_STATUS_MAP`) and the OutreachPage frontend
// produce identical counts. Without this normalization, a Taisho × "Not
// Interested" account row was counted by the frontend but dropped by the
// backend (canonical → "Declined" → asset-level + no asset → skipped),
// causing a 1-row mismatch between "All Conversations" pill and page.
export const ACCT_STATUS_MAP = {
  'Closed-Won': 'Deal Closed',
  'Closed - Won': 'Deal Closed',
  'Closed-Successful': 'Deal Closed',
  'Closed-Lost': 'Deal Lost',
  'Closed - Lost': 'Deal Lost',
  'Not Interested': 'Declined',
  'NCD Exchanged': 'CDA Signed',
};
export const canonicalizeStatus = (s) => (s && ACCT_STATUS_MAP[s]) || s;

// Accounts module status vocabulary. Different terms ("Not Interested",
// "Closed-Won", "NCD Exchanged") map to the same canonical hues used by
// the outreach pipeline so a "Not Interested" badge on the Accounts page
// reads the same color as a "Declined" badge on Outreach. Statuses
// unique to Accounts (Interest Shown, Already in Touch, Met In-Person)
// get their own distinct hue.
export const ACCOUNT_STATUS_COLORS = {
  'Introduction Made':   'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Email Acknowledged':  'bg-green-100 text-green-700 border-green-200',
  'Introduced':          'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Already in Touch':    'bg-teal-100 text-teal-700 border-teal-200',
  'Met In-Person':       'bg-sky-100 text-sky-700 border-sky-200',
  'Interest Shown':      'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Meeting Requested':              'bg-violet-50 text-violet-600 border-violet-200',
  'Meeting Scheduled':              'bg-violet-100 text-violet-700 border-violet-200',
  'Meeting Completed':              'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  'CDA in Progress':                'bg-blue-50 text-blue-600 border-blue-200',
  'CDA Signed':                     'bg-blue-100 text-blue-700 border-blue-200',
  'NCD Exchanged':                  'bg-blue-100 text-blue-700 border-blue-200',
  'Data Shared / Data Room Access': 'bg-sky-100 text-sky-700 border-sky-200',
  'Awaiting Trial Data':            'bg-orange-100 text-orange-700 border-orange-200',
  'Due Diligence':                  'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Business Discussion':            'bg-pink-100 text-pink-700 border-pink-200',
  'Deal Discussion':                'bg-purple-100 text-purple-700 border-purple-200',
  'Term Sheet Shared':              'bg-lime-100 text-lime-700 border-lime-200',
  'Deal Closed':         'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Closed-Won':          'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Closed-Successful':   'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Not Interested':      'bg-rose-100 text-rose-700 border-rose-200',
  'Declined':            'bg-rose-100 text-rose-700 border-rose-200',
  'Closed-Lost':         'bg-rose-100 text-rose-700 border-rose-200',
  'Deal Lost':           'bg-rose-100 text-rose-700 border-rose-200',
  'On Hold':             'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Ghosted':             'bg-stone-100 text-stone-700 border-stone-200',
};

export const getAccountStatusColor = (status) =>
  ACCOUNT_STATUS_COLORS[status] || 'bg-slate-100 text-slate-600 border-slate-200';

// Feb 2026 (v6) — per user's explicit ask: use the exact canonical
// status name everywhere so dashboard tile text ↔ page header ↔ URL
// param all read identically. Prior plural-for-readability overrides
// ("Assets Under Evaluation", "Meetings Scheduled") were removed to
// avoid the mental double-take of "why did the tile pluralize?".
const STATUS_LABEL_OVERRIDES = {};

export const statusLabel = (status) => STATUS_LABEL_OVERRIDES[status] || status;
